import { settingsPagePath } from "@/components/sidebar/useActiveApp";
import type { AssistantSessionSummary } from "@/components/codesmith/types";

export const CODESMITH_CONVERSATION_ACTION_EVENT =
  "codesmithConversationAction";

export type CodesmithConversationAction = "archive" | "unarchive";

/** The palette only listens for its own shortcut, so buttons replay it. */
export function openCommandPalette() {
  document.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "k",
      metaKey: true,
      bubbles: true,
      cancelable: true,
    }),
  );
}

export function currentCodesmithConversationAction({
  sessionId,
  session,
  userId,
}: {
  sessionId: string | null | undefined;
  session: AssistantSessionSummary | undefined;
  userId: number | null | undefined;
}): { sessionId: string; action: CodesmithConversationAction } | null {
  if (
    !sessionId ||
    session?.id !== sessionId ||
    session.status === "forking" ||
    session.status === "dead"
  ) {
    return null;
  }
  if (
    userId == null ||
    (session.user_id != null && session.user_id !== userId)
  ) {
    return null;
  }

  return {
    sessionId,
    action: session.status === "archived" ? "unarchive" : "archive",
  };
}

function usageTabPath(
  org: string | null | undefined,
  tab: "codesmith" | "github-actions",
): string {
  const path = org ? `/${org}/usage` : "/usage";
  return `${path}?tab=${tab}`;
}

export function codesmithUsagePath(org: string | null | undefined): string {
  return usageTabPath(org, "codesmith");
}

export function githubActionsUsagePath(org: string | null | undefined): string {
  return usageTabPath(org, "github-actions");
}

/**
 * Resolves a global keydown to a settings navigation path, or null when the
 * event is not the settings shortcut (Command+, on macOS, Ctrl+, elsewhere).
 */
export function settingsShortcutPath(
  event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey">,
  org: string | null | undefined,
  isCodesmithPage: boolean,
): string | null {
  if (event.key !== "," || !(event.metaKey || event.ctrlKey)) return null;
  return settingsPagePath(org, isCodesmithPage);
}
